import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSyncSchedule, updateSyncSchedule } from "../../api/syncStatus";
import { ToggleSwitch } from "../shared/ToggleSwitch";
import { ErrorState, LoadingState } from "../states";
import { useToast } from "../toast/toastContext";
import panel from "./AdminPanel.module.css";
import styles from "./SyncSchedulePanel.module.css";

const SettingsWritePermission = "settings.write";

/**
 * Whether the timer-driven vendor syncs run, on the Sync status screen beneath the providers they sync: a switch in the panel's header,
 * with what turning it off does beneath. Manual syncs work either way. Needs `settings.write`, as the screen's sync actions do.
 */
export function SyncSchedulePanel(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; enabled: boolean } | { kind: "error"; message: string }>({ kind: "loading" });
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchSyncSchedule();
    if (result.kind === "ok") {
      setState({ kind: "loaded", enabled: result.response.enabled });
    } else {
      setState({ kind: "error", message: result.kind === "denied" ? t("admin.denied.description", { permissions: SettingsWritePermission }) : result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (enabled: boolean) => {
    setPending(true);
    const result = await updateSyncSchedule(enabled);
    setPending(false);
    if (result.kind === "ok") {
      setState({ kind: "loaded", enabled: result.response.enabled });
      toast.show(result.response.enabled ? t("admin.syncStatus.schedule.turnedOn") : t("admin.syncStatus.schedule.turnedOff"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <section className={panel.panel} aria-labelledby="sync-schedule-heading">
      <div className={panel.header}>
        <h2 id="sync-schedule-heading" className={`panel-title ${panel.title}`}>
          {t("admin.syncStatus.schedule.title")}
        </h2>
        {state.kind === "loaded" && (
          <span className={styles.control}>
            <span id="sync-schedule-state" className={styles.state} data-on={state.enabled || undefined}>
              {state.enabled ? t("admin.syncStatus.schedule.on") : t("admin.syncStatus.schedule.off")}
            </span>
            <span id="sync-schedule-label" className="visually-hidden">
              {t("admin.syncStatus.schedule.enabled")}
            </span>
            <ToggleSwitch checked={state.enabled} disabled={pending} labelledBy="sync-schedule-label" onChange={(enabled) => void toggle(enabled)} />
          </span>
        )}
      </div>
      {state.kind === "loading" && <LoadingState label={t("admin.syncStatus.schedule.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && <p className={panel.intro}>{t("admin.syncStatus.schedule.explanation")}</p>}
    </section>
  );
}
